import Avo from '@susu/custom-made/Avo';
import { useCallback } from 'react';

import { productCodeByConfigurationId, type ConfigurationTypes } from '../utils/productDefinition';
import { useMissingValues } from './useMissingValues';
import useNavigationContext from '../providers/hooks/useNavigationContext';
import useOptionContext from '../providers/hooks/useOptionContext';
import { useAvo } from './useAvo';
import { Section } from '../utils/navigation';
import { camelToSnakeCase } from '../utils/utils';
import { useFeatureIsOn } from '@susu/feature-flags/react';
import { FEATURE_TOGGLES } from '../feature-flags/featureToggles';

const { ENABLE_AVO_TRACKING } = FEATURE_TOGGLES;

export const useAdditionalProduct = () => {
  const { section } = useNavigationContext();
  const { removeConfigurationOptionValuesFromMissingValues } = useMissingValues();
  const { addRemoveAdditionalProduct, selectedBaseStyle } = useOptionContext();
  const { getAvoStandardEventParams } = useAvo();
  const isAvoTrackingEnabled = useFeatureIsOn(ENABLE_AVO_TRACKING);

  const addAdditionalProductHandler = useCallback(
    (affectedConfigurationTypeId: ConfigurationTypes) => {
      addRemoveAdditionalProduct(affectedConfigurationTypeId, true);

      if (isAvoTrackingEnabled) {
        Avo.cmCustomizationAdded({
          ...getAvoStandardEventParams(`${section}${section !== Section.Summary ? '_options' : ''}`),
          cmItemType: Avo.CmItemType.PRODUCT,
          currentBaseStyle: selectedBaseStyle[affectedConfigurationTypeId]?.name?.toLowerCase(),
          cmItemValue: camelToSnakeCase(productCodeByConfigurationId[affectedConfigurationTypeId]),
        });
      }
    },
    [isAvoTrackingEnabled, section, addRemoveAdditionalProduct, getAvoStandardEventParams, selectedBaseStyle],
  );

  const removeAdditionalProductHandler = useCallback(
    (affectedConfigurationTypeId: ConfigurationTypes) => {
      addRemoveAdditionalProduct(affectedConfigurationTypeId, false);

      if (isAvoTrackingEnabled) {
        Avo.cmCustomizationDeleteConfirmed({
          ...getAvoStandardEventParams(`${section}${section !== Section.Summary ? '_options' : ''}`),
          cmItemType: Avo.CmItemType.PRODUCT,
          currentBaseStyle: selectedBaseStyle[affectedConfigurationTypeId]?.name?.toLowerCase(),
          cmItemValue: camelToSnakeCase(productCodeByConfigurationId[affectedConfigurationTypeId]),
        });
      }

      removeConfigurationOptionValuesFromMissingValues(affectedConfigurationTypeId);
      // To trigger sticky image animation builder and scroll into proper summary section view
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('forceScrollRecalculation'));
      }, 100);
    },
    [isAvoTrackingEnabled, section, selectedBaseStyle, addRemoveAdditionalProduct, removeConfigurationOptionValuesFromMissingValues, getAvoStandardEventParams],
  );

  return { addAdditionalProductHandler, removeAdditionalProductHandler };
};
